import { Controller, Loader, IDynamicLoader } from '@tencent/workbench';
import { Form, NumberInputFrom } from '@tencent/wb-ext-form';
import { FormItemName, IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { FORM_CONFIGS, NUMBER_INPUT_OPTION } from '@framework/workbench/drawer/ui/text/text-box/config';
import { WrapType, FillType, TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { ITextBoxFeature } from './text-box-interface';

@Controller({ formItems: FORM_CONFIGS })
export class TextBoxController {
    constructor(@Loader(ITextBoxFeature) private loader: IDynamicLoader<ITextBoxFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(FormItemName.FONT_ADJUST, 'value')
    public getAdjust(): number {
        return this.feature.textAdjustInfo?.autoFitType;
    }

    @Form(FormItemName.FONT_ADJUST, 'onChange')
    public changeAdjust(value: number): void {
        this.feature.updateProps(
            ActionType.TEXT_ADJUST, { autoFitType: value }, false, false,
            { reportId: FormItemName.FONT_ADJUST, ver5: value },
        );
    }

    @Form(FormItemName.TEXT_DIRECTION, 'value')
    public getTextDirection(): TextDirectionType {
        const { textDirection } = this.feature;
        // 两种堆积对应一个选项，都显示为堆积
        if (textDirection === TextDirectionType.WORD_ART_VERTICAL_LTR
            || textDirection === TextDirectionType.WORD_ART_VERTICAL_RTL) {
            return TextDirectionType.WORD_ART_VERTICAL_GENERAL;
        }
        return textDirection;
    }

    @Form(FormItemName.TEXT_DIRECTION, 'onChange')
    public changeTextDirection(value: TextDirectionType): void {
        // 选中堆积选项时，默认行顺序位从右向左
        const textDirection = value === TextDirectionType.WORD_ART_VERTICAL_GENERAL
            ? TextDirectionType.WORD_ART_VERTICAL_RTL : value;
        this.feature.updateProps(
            FormItemName.TEXT_DIRECTION, textDirection, false, false,
            { reportId: FormItemName.TEXT_DIRECTION, ver5: textDirection },
        );
    }

    @Form(FormItemName.ORDER_OF_LINE, 'value')
    public getOrderOfLine(): TextDirectionType {
        return this.feature.textDirection;
    }

    @Form(FormItemName.ORDER_OF_LINE, 'onChange')
    public changeOrderOfLine(value: TextDirectionType): void {
        this.feature.updateProps(
            FormItemName.TEXT_DIRECTION, value, false, false,
            { reportId: FormItemName.TEXT_DIRECTION, ver5: value },
        );
    }

    @Form(FormItemName.FONT_PADDING_LEFT, 'value')
    public getLeft(): number | IMultiDiffValue {
        return this.getPaddingValue(FormItemName.FONT_PADDING_LEFT);
    }

    @Form(FormItemName.FONT_PADDING_LEFT, 'onChange')
    public changeLeft(value: number, from?: NumberInputFrom): void {
        this.handlePaddingChange(FormItemName.FONT_PADDING_LEFT, value, from);
    }

    @Form(FormItemName.FONT_PADDING_RIGHT, 'value')
    public getRight(): number | IMultiDiffValue {
        return this.getPaddingValue(FormItemName.FONT_PADDING_RIGHT);
    }

    @Form(FormItemName.FONT_PADDING_RIGHT, 'onChange')
    public changeRight(value: number, from?: NumberInputFrom): void {
        this.handlePaddingChange(FormItemName.FONT_PADDING_RIGHT, value, from);
    }

    @Form(FormItemName.FONT_PADDING_TOP, 'value')
    public getTop(): number | IMultiDiffValue {
        return this.getPaddingValue(FormItemName.FONT_PADDING_TOP);
    }

    @Form(FormItemName.FONT_PADDING_TOP, 'onChange')
    public changeTop(value: number, from?: NumberInputFrom): void {
        this.handlePaddingChange(FormItemName.FONT_PADDING_TOP, value, from);
    }

    @Form(FormItemName.FONT_PADDING_BOTTOM, 'value')
    public getBottom(): number | IMultiDiffValue {
        return this.getPaddingValue(FormItemName.FONT_PADDING_BOTTOM);
    }

    @Form(FormItemName.FONT_PADDING_BOTTOM, 'onChange')
    public changeBottom(value: number, from?: NumberInputFrom): void {
        this.handlePaddingChange(FormItemName.FONT_PADDING_BOTTOM, value, from);
    }


    @Form(FormItemName.FONT_AUTO_BREAK, 'value')
    public getAutoBreakValue(): boolean {
        return this.getAutoBreak(this.feature.textAdjustInfo?.autoBreakType);
    }

    @Form(FormItemName.FONT_AUTO_BREAK, 'onChange')
    public changeAutoBreakValue(value: string): void {
        const autoBreakType = value ? WrapType.WRAP : WrapType.NO_WRAP;
        this.feature.updateProps(
            ActionType.TEXT_ADJUST, { autoBreakType }, false, false,
            { reportId: ActionType.TEXT_ADJUST },
        );
    }

    // @ts-ignore
    @Form(FormItemName.FONT_AUTO_BREAK, 'options')
    public getOptions() {
        return {
            fillType: this.getFillType(this.feature.textAdjustInfo?.autoBreakType),
        };
    }

    private getPaddingValue = (type: string) => {
        const value = this.feature.getShownValue(
            INPUT_PROPS_CALLBACK_KEY.TEXT_BOX,
            { [type]: this.feature.paddingProps?.[type] },
        );
        const number = parseFloat(String(value));
        return isNaN(number) ? value : number;
    };

    private handlePaddingChange = (type: string, value: number, from?: NumberInputFrom) => {
        value = this.feature.getValidValue({ [type]: value }, from, NUMBER_INPUT_OPTION.MAX)[type];
        this.feature.handleChange(type, value, from);
    };

    private getAutoBreak = autoBreakType => autoBreakType === WrapType.WRAP || autoBreakType === WrapType.MULTI;
    private getFillType = autoBreakType => (autoBreakType === WrapType.MULTI ? FillType : undefined);
}
